// Next Imports
import Link from "next/link";
import Head from "next/head";

// pages/index.js
import {
  Typography,
  Grid2 as Grid,
  Box,
  Button,
  Container,
  Card,
  CardContent,
} from "@mui/material";
import Image from "next/image";

// ** Layout
import DefaultLayout from "../../layout/DefaultLayout";

function About() {
  return (
    <>
      <Head>
        <title>About | Valentin Marko</title>

        <meta
          name="description"
          content="About Valentin-Marko Cîmpan — full-stack software developer. My experience, background, and how I work."
        />

        <meta name="robots" content="index, follow" />

        {/* Canonical */}
        <link rel="canonical" href="https://valentinmarko.com/about" />

        {/* Open Graph */}
        <meta property="og:title" content="About | Valentin Marko" />
        <meta
          property="og:description"
          content="Full-stack software developer. My experience, background, and how I work."
        />
        <meta property="og:type" content="profile" />
        <meta property="og:url" content="https://valentinmarko.com/about" />
        <meta
          property="og:image"
          content="https://valentinmarko.com/about.webp"
        />
      </Head>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Person",
            "@id": "https://valentinmarko.com/#person",
            name: "Valentin-Marko Cîmpan",
            url: "https://valentinmarko.com/about",
            sameAs: ["https://www.linkedin.com/in/marko-cimpan/"],
            jobTitle: "Full Stack Developer",
          }),
        }}
      />

      <Container>
        <Grid container spacing={3} marginTop={"34px"} marginBottom={"34px"}>
          <Grid size={{ xs: 12 }}>
            <Typography
              component={"h1"}
              variant="h1"
              gutterBottom
              sx={{
                fontSize: { xs: "25px", sm: "35px", lg: "44px" },
                fontWeight: 700,
                color: "#2D3748",
                textTransform: "capitalize",
              }}
            >
              About me
            </Typography>
            <Typography
              component={"p"}
              variant="h5"
              sx={{
                fontSize: "20px",
                fontWeight: 400,
                color: "rgb(113, 128, 150)",
                marginBottom: "30px",
              }}
            >
              In software development, there’s no such thing as ‘can’t’. It’s
              just a matter of time, debugging, and iteration before you make it
              work.
            </Typography>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Box
              sx={{
                position: "relative",
                width: "100%",
                height: "450px", // Set the height for the container
                overflow: "hidden",
                borderRadius: "20px", // Rounded corners
              }}
            >
              <Image
                src={"/about.webp"}
                alt="Valentin Marko’s software development workstation"
                layout="fill" // Make the image fill the container
                objectFit="cover" // Cover the entire container area
                quality={50}
                priority
              />
              <Box
                sx={{
                  position: "absolute",
                  bottom: 26,
                  right: 26,
                }}
              >
                <Button
                  component={Link}
                  href="/contact"
                  sx={{
                    borderRadius: "30px",
                    padding: "8px 24px",
                    backgroundColor: "#667EEA",
                    color: "#ffffff",
                    textTransform: "none",
                    fontSize: "16px",
                    fontWeight: 400,
                    marginRight: "10px",
                    "&:hover": {
                      backgroundColor: "#7F8DA1", // Prevents background change
                    },
                  }}
                >
                  Contact Me
                </Button>
                <Button
                  component={Link}
                  href="https://storage.googleapis.com/valentin-marko-blog/Valentin-Marko%20CV.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    borderRadius: "30px",
                    padding: "8px 24px",
                    backgroundColor: "#667EEA",
                    color: "#ffffff",
                    textTransform: "none",
                    fontSize: "16px",
                    fontWeight: 400,
                    marginRight: "10px",
                    "&:hover": {
                      backgroundColor: "#7F8DA1", // Prevents background change
                    },
                  }}
                >
                  See CV
                </Button>
              </Box>
            </Box>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Box
              sx={{
                display: "flex",
                marginTop: "30px",
                alignItems: "center",
                marginBottom: "60px",
              }}
            >
              <Box
                sx={{
                  borderLeft: "3px solid rgb(226, 232, 240)",
                  borderRight: "3px solid rgb(226, 232, 240)",

                  width: "0px",
                  height: "35px",
                  borderRadius: "50px",
                  marginRight: "30px",
                }}
              ></Box>
              <Typography
                variant="h2"
                component={"h2"}
                sx={{
                  fontSize: "24px",
                  fontWeight: 900,
                  color: "#2D3748",
                  marginRight: "8px",
                  fontWeight: 700,
                }}
              >
                My story
              </Typography>
            </Box>
            <Typography
              sx={{
                color: "rgb(113, 128, 150)",
                fontWeight: 400,
                fontSize: "16px",
                marginBottom: "15px",
              }}
            >
              Throughout my career, I’ve collaborated on large-scale projects
              with teams of 20+ engineers, as well as fast-paced startups with
              small teams of 1–4 people. I’ve also build and delivered
              end-to-end projects independently, taking full ownership of both
              frontend and backend development, from initial architecture and
              implementation to deployment and maintenance.
            </Typography>
            <Typography
              sx={{
                color: "rgb(113, 128, 150)",
                fontWeight: 400,
                fontSize: "16px",
                marginBottom: "15px",
              }}
            >
              I am a full-stack developer with experience building modern,
              scalable web applications using both monolithic and microservice
              architectures. On the frontend, I specialize in React and Next.js,
              working extensively with TypeScript and JavaScript, as well as
              state-management solutions such as Redux, Redux Toolkit, and React
              Query. I focus on creating responsive, accessible, and
              high-performance user interfaces using Tailwind CSS and Material
              UI.
            </Typography>
            <Typography
              sx={{
                color: "rgb(113, 128, 150)",
                fontWeight: 400,
                fontSize: "16px",
                marginBottom: "15px",
              }}
            >
              I’ve worked closely with UI/UX designers using tools like Figma
              and Zeplin, translating design systems and prototypes into
              polished, interactive components with strong attention to detail
              and user experience.
            </Typography>
            <Typography
              sx={{
                color: "rgb(113, 128, 150)",
                fontWeight: 400,
                fontSize: "16px",
                marginBottom: "15px",
              }}
            >
              On the backend, I’ve built and maintained APIs and services using
              Node.js and NestJS, working with both relational databases such as
              PostgreSQL and NoSQL databases like MongoDB. I also have
              experience designing event-driven microservices using RabbitMQ to
              enable scalable, modular, and reliable systems.
            </Typography>
          </Grid>
          <Grid size={{ xs: 6 }} sx={{ display: { xs: "none", md: "block" } }}>
            <Box
              sx={{
                display: "flex",
                marginTop: "30px",
                alignItems: "center",
                marginBottom: "60px",
              }}
            >
              <Box
                sx={{
                  borderLeft: "3px solid rgb(226, 232, 240)",
                  borderRight: "3px solid rgb(226, 232, 240)",

                  width: "0px",
                  height: "35px",
                  borderRadius: "50px",
                  marginRight: "30px",
                }}
              ></Box>
              <Typography
                variant="h2"
                component={"h2"}
                sx={{
                  fontSize: "24px",
                  fontWeight: 900,
                  color: "#2D3748",
                  marginRight: "8px",
                  fontWeight: 700,
                }}
              >
                Frontend Development
              </Typography>
            </Box>
            <Card sx={{ borderRadius: "15px" }}>
              {" "}
              <CardContent sx={{ padding: "30px" }}>
                {" "}
                <Typography
                  sx={{
                    color: "rgb(113, 128, 150)",
                    fontWeight: 400,
                    fontSize: "16px",
                  }}
                >
                  Building modern, responsive, and accessible user interfaces
                  with a focus on performance, scalability, and great user
                  experience. Strong experience with state management, data
                  fetching, and design systems.
                </Typography>{" "}
              </CardContent>{" "}
            </Card>{" "}
            <Box
              sx={{
                display: "flex",
                marginTop: "60px",
                alignItems: "center",
                marginBottom: "60px",
              }}
            >
              <Box
                sx={{
                  borderLeft: "3px solid rgb(226, 232, 240)",
                  borderRight: "3px solid rgb(226, 232, 240)",

                  width: "0px",
                  height: "35px",
                  borderRadius: "50px",
                  marginRight: "30px",
                }}
              ></Box>
              <Typography
                variant="h2"
                component={"h2"}
                sx={{
                  fontSize: "24px",
                  fontWeight: 900,
                  color: "#2D3748",
                  marginRight: "8px",
                  fontWeight: 700,
                }}
              >
                Backend Development
              </Typography>
            </Box>
            <Card sx={{ borderRadius: "15px" }}>
              {" "}
              <CardContent sx={{ padding: "30px" }}>
                {" "}
                <Typography
                  sx={{
                    color: "rgb(113, 128, 150)",
                    fontWeight: 400,
                    fontSize: "16px",
                  }}
                >
                  Designing and maintaining scalable APIs and services, working
                  with both SQL and NoSQL databases, and implementing clean,
                  maintainable backend architectures.
                </Typography>{" "}
              </CardContent>{" "}
            </Card>{" "}
            <Box
              sx={{
                display: "flex",
                marginTop: "60px",
                alignItems: "center",
                marginBottom: "60px",
              }}
            >
              <Box
                sx={{
                  borderLeft: "3px solid rgb(226, 232, 240)",
                  borderRight: "3px solid rgb(226, 232, 240)",

                  width: "0px",
                  height: "35px",
                  borderRadius: "50px",
                  marginRight: "30px",
                }}
              ></Box>
              <Typography
                variant="h2"
                component={"h2"}
                sx={{
                  fontSize: "24px",
                  fontWeight: 900,
                  color: "#2D3748",
                  marginRight: "8px",
                  fontWeight: 700,
                }}
              >
                Mobile Development
              </Typography>
            </Box>
            <Card sx={{ borderRadius: "15px" }}>
              {" "}
              <CardContent sx={{ padding: "30px" }}>
                {" "}
                <Typography
                  sx={{
                    color: "rgb(113, 128, 150)",
                    fontWeight: 400,
                    fontSize: "16px",
                  }}
                >
                  Developing cross-platform mobile applications with shared
                  business logic and consistent user experience across iOS and
                  Android.
                </Typography>{" "}
              </CardContent>{" "}
            </Card>{" "}
          </Grid>
        </Grid>
      </Container>
    </>
  );
}

About.getLayout = (page) => <DefaultLayout>{page}</DefaultLayout>;

export default About;
